import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import LmBulkDialog, { OwnerSelect } from '../list-management/LmBulkDialog';
import { lmError } from '../list-management/lmApi';
import { copyToClipboard } from '../../lib/clipboard';
import { createOwnerOperationsApi, quotaLine, waitFor } from './ownerOperationsApi';

/**
 * Reassign Contact Owner for the selected contacts (#863, epic #860).
 *
 * 1. Choose the new Contact Owner → `POST /owner-operations/preview` pins every
 *    row's current owner and version (a request counter drops out-of-order
 *    previews). The preview shows change / no-op / not-found counts, the first
 *    20 rows and the caller's daily quota.
 * 2. A reason is required for every bulk change; Confirm stays disabled until
 *    the preview has something to change and the reason is filled in.
 * 3. Within quota the commit runs in the background and the dialog polls it to
 *    the end; over quota it becomes ONE request a different admin approves
 *    ("Request approval"). Nothing is partly committed.
 * 4. Failures are final for an operation (`retryable` is off): a contact
 *    changed since the preview is never overwritten. `onDone` reselects the
 *    failed contacts so one click re-previews them against their current owner.
 *    Any change that went through offers Undo (admins) or the operation ID; an
 *    Undo restores only the contacts it changed.
 *
 * Contacts are named from the preview rows and the results envelope (`name`),
 * falling back to `labelFor`: the page reloads its contacts right after a run,
 * so its list may no longer hold them. The commit can be left running in the
 * background (LmBulkDialog `background`): that toasts that it keeps running and
 * calls `onDone(null, { background: true })` — there is no outcome yet, so the
 * page clears its selection and reloads now and again once the run is done.
 */
const PREVIEW_ROWS = 20;

export default function ReassignOwnerDialog({
    open, onClose, contactIds, labelFor, owners, getAuthHeader, canRestore, onDone, onUndo, onSelectionChanged,
}) {
    const api = useMemo(() => createOwnerOperationsApi(getAuthHeader), [getAuthHeader]);
    const [ownerId, setOwnerId] = useState('');
    const [reason, setReason] = useState('');
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const seq = useRef(0);
    const alive = useRef(true);
    const names = useRef({});

    useEffect(() => () => { alive.current = false; }, []);
    useEffect(() => {
        if (open) {
            setOwnerId(''); setReason(''); setPreview(null); setPreviewError(null); alive.current = true;
            names.current = {};
        }
    }, [open]);

    const remember = (rows, idKey) => (rows || []).forEach((r) => {
        if (r.name && r.name !== r[idKey]) names.current[r[idKey]] = r.name;  // a not-found row is named by its id
    });
    const nameFor = (id) => names.current[id] || (labelFor ? labelFor(id) : id);

    const choose = async (value) => {
        setOwnerId(value);
        setPreview(null);
        setPreviewError(null);
        // Every choice, clearing included, drops the preview still in flight.
        const mine = ++seq.current;
        setPreviewing(!!value);
        if (!value) return;
        try {
            const res = await api.preview(contactIds, value);
            if (mine === seq.current) {
                remember(res.rows, 'contact_id');
                setPreview(res);
            }
        } catch (err) {
            if (mine === seq.current) setPreviewError(lmError(err, 'The preview could not be built.'));
        } finally {
            if (mine === seq.current) setPreviewing(false);
        }
    };

    const eligible = (owners || []).filter((o) => o.approval_status !== 'pending' && o.is_active !== false);
    const changed = preview?.counts?.changed || 0;
    const needsApproval = !!preview?.approval_required;

    const run = async (_sel, { isAlive } = {}) => {
        const opId = preview.operation_id;
        const res = await api.commit(opId, { reason: reason.trim(), new_owner_id: ownerId, expected_changed: changed });
        if (res.status === 'pending_approval') return { succeeded: 0, failed: 0, results: [], pending: true, operation_id: opId };
        const view = await waitFor(api, opId, { isAlive: () => alive.current && (!isAlive || isAlive()) });
        remember(view.results, 'row_id');
        return { ...view, operation_id: opId };
    };

    const done = (res) => {
        if (res?.pending) {
            toast.success('Sent for approval to an admin');
        } else if (res && (res.counts?.changed ?? res.succeeded) > 0) {
            // A partial run offers the same toast: its Undo restores only what
            // changed. `succeeded` also counts no-ops, so the run's own counts decide.
            const counts = res.counts || {};
            const n = counts.changed ?? res.succeeded;
            const message = `${n} Contact Owner${n === 1 ? '' : 's'} changed`;
            const keptN = res.counts ? (counts.conflict || 0) + (counts.failed || 0) : res.failed;
            const missing = counts.invalid || 0;
            const kept = (keptN > 0 ? `${keptN} kept their Contact Owner. ` : '')
                + (missing > 0 ? `${missing} ${missing === 1 ? 'was' : 'were'} not found. ` : '');
            if (canRestore) {
                toast.success(message, {
                    ...(kept ? { description: `${kept}Undo restores only the ones that changed.` } : {}),
                    action: { label: 'Undo', onClick: () => onUndo?.(res.operation_id) },
                    duration: 10000,
                });
            } else {
                toast.success(message, {
                    description: `${kept}Only an admin can undo this change.`,
                    action: { label: 'Copy operation ID', onClick: () => copyToClipboard(res.operation_id) },
                    duration: 10000,
                });
            }
        }
        onDone?.(res);
    };
    // Closed into the background (or polling gave up): the run finishes on the server.
    const background = () => {
        toast.info('The Contact Owner change keeps running in the background. The list updates when it finishes.');
        onDone?.(null, { background: true });
    };

    const rows = (preview?.rows || []).slice(0, PREVIEW_ROWS);
    const extra = (
        <div className="space-y-3">
            <OwnerSelect owners={eligible} value={ownerId} onChange={choose} id="reassign-owner-select"
                         label="New Contact Owner" placeholder="Choose a teammate…" />
            {previewing && (
                <div className="flex items-center gap-2 text-[12.5px] text-[#8A837E]" role="status">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Checking the selected contacts…
                </div>
            )}
            {previewError && <p role="alert" className="text-[12.5px] text-[#B0132A]">{previewError}</p>}
            {preview && (
                <div className="space-y-2" data-testid="reassign-preview">
                    <div className="flex gap-4 text-[12.5px] text-[#3A3532] tabular-nums">
                        <span><b>{preview.counts.changed}</b> change</span>
                        <span><b>{preview.counts.noop}</b> already owned</span>
                        <span><b>{preview.counts.not_found}</b> not found</span>
                    </div>
                    {rows.length > 0 && (
                        <ul className="max-h-[180px] overflow-y-auto border border-[#EDEBE6] rounded-[10px] divide-y divide-[#F4F2EE]">
                            {rows.map((r) => (
                                <li key={r.contact_id} className="px-3 py-1.5 text-[12.5px] text-[#3A3532]">
                                    <span className="font-medium text-[#0C0A09]">{nameFor(r.contact_id)}</span>
                                    {' · '}
                                    {r.outcome_preview === 'not_found' ? 'Not found'
                                        : r.outcome_preview === 'noop' ? 'Already owned'
                                            : `${r.current_owner_label || 'No Contact Owner'} → ${preview.new_owner_label || 'new owner'}`}
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="text-[12px] text-[#8A837E]" data-testid="reassign-quota">{quotaLine(preview.quota)}</p>
                    {needsApproval && changed > 0 && (
                        <p className="text-[12.5px] text-[#8A6516]" data-testid="reassign-needs-approval">
                            This is more than your daily limit, so it goes to an admin for approval. Nothing changes until it is approved.
                        </p>
                    )}
                </div>
            )}
            <label className="block">
                <span className="text-[12.5px] font-medium text-[#3A3532]">Reason</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Reason for the change"
                       data-testid="reassign-reason" placeholder="Why are these contacts changing owner?"
                       className="mt-1 w-full h-[38px] px-3 border border-[#E4E1DB] rounded-[9px] text-[13px] outline-none focus:border-[#1C1917]" />
            </label>
        </div>
    );

    return (
        <LmBulkDialog
            open={open}
            onClose={onClose}
            title="Reassign Contact Owner"
            description="Changes the Contact Owner of the selected contacts. A contact someone else changed since the preview keeps its current Contact Owner."
            confirmLabel={needsApproval ? 'Request approval' : `Reassign ${changed} contact${changed === 1 ? '' : 's'}`}
            tone="dark"
            selection={{ row_ids: contactIds }}
            count={contactIds.length}
            run={run}
            background
            onBackground={background}
            extra={extra}
            confirmDisabled={!preview || changed === 0 || !reason.trim() || previewing}
            confirmHint="Choose a new Contact Owner and give a reason"
            labelFor={nameFor}
            retryable={() => false}
            onDone={done}
            onSelectionChanged={() => {
                toast.error('The preview changed — review the contacts again');
                onSelectionChanged?.();
            }}
        />
    );
}
